import React from 'react';
import styled from 'styled-components';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import InputComponent from 'components/UI/Input';
import Checkbox from 'components/UI/Checkbox';
import { Main, Text } from 'styles/themeColors';
import {
    ERROR_MESSAGES,
    SECTION_TITLES,
    INPUT_FIELDS,
} from 'constants/emailConfiguration';
import Vendor from 'data/Vendor';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { Color } from 'lib/styles';
import { useEmailConfigurationForm } from '../hooks/useEmailConfigurationForm';
import ProductLocalization from 'components/Product/ProductLocalization';
import SelectComponent from 'components/UI/Select';
import { BusinessUnit } from 'data/BusinessUnit';
import { useEmailConfigurationFormV2 } from 'hooks/useEmailConfigurationFormV2';
import FeatureFlag from 'data/FeatureFlag';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

const CheckboxContainer = styled.div`
    margin-top: 20px;
`;

const Description = styled.p<{ disabled?: boolean }>`
    margin: 0;
    font-size: 14px;
    line-height: 18px;
    color: ${({ disabled }) => (disabled ? Text.disabled : Text.primary)};
`;

const InputContainer = styled.div`
    margin-top: 15px;
    margin-bottom: 20px;
`;

// Container for inline inputs
const InlineInputsContainer = styled.div`
    display: flex;
    gap: 20px;
    margin-top: 15px;
    margin-bottom: 10px;
`;

// Container for each inline input and its description
const InlineInputBox = styled.div`
    flex: 1;
`;

const SectionTitle = styled.p`
    font-size: 20px;
    margin-top: 0px;
    margin-bottom: 0px;
    font-weight: 400;
    color: ${Text.primary};
`;

const EmptyMessage = styled.span`
  font-size: 20px;
  padding: 0px;
`;

interface EmailConfigProps {
    setIsSaveDisabled: (disabled: boolean) => void;
    setHasUnsavedChanges: (hasChanges: boolean) => void;
    setReadOnly?: (readOnly: boolean) => void;
    selectedVendor: Vendor;
    disabled?: boolean;
    selectedBusinessUnit?: BusinessUnit;
    featureFlags?: FeatureFlag;
    emailRemindersReadyOnly?: boolean;
    paymentRemindersReadyOnly?: boolean;
}

// Main component
const EmailConfigurationSettings = React.forwardRef<
    { handleSave: () => void },
    EmailConfigProps
>(({ setIsSaveDisabled,
    setHasUnsavedChanges,
    setReadOnly,
    selectedVendor,
    disabled,
    selectedBusinessUnit,
    featureFlags,
    emailRemindersReadyOnly,
    paymentRemindersReadyOnly
}, ref) => {
    const {
        configuration: vendorConfiguration,
        errors,
        isLoading,
        isAutoApproveDisabled,
        brandList,
        handleRentReminderChange,
        handleFirstPaymentReminderChange,
        handleSecondPaymentReminderChange,
        handleFinalPaymentReminderChange,
        handleAutoApproveAfterFinalChange,
        handleAutoApproveChange,
        handleSave,
        handleBrandChange
    } = featureFlags?.businessUnit?.enabled ? useEmailConfigurationFormV2({
        selectedBusinessUnit,
        setIsSaveDisabled,
        setHasUnsavedChanges,
        setReadOnly
    }) : useEmailConfigurationForm({
        selectedVendor,
        setIsSaveDisabled,
        setHasUnsavedChanges,
        setReadOnly
    });

    // Expose save method to parent component via ref
    React.useImperativeHandle(ref, () => ({
        handleSave
    }));

    if (!selectedVendor) {
        return (
            <FlexboxContainerComponent flexDirection="column">
                <EmptyMessage>{ERROR_MESSAGES.NO_VENDOR}</EmptyMessage>
            </FlexboxContainerComponent>
        );
    }

    if (featureFlags?.businessUnit?.enabled && !selectedBusinessUnit)
        return (
            <FlexboxContainerComponent flexDirection="column">
                <EmptyMessage>{ERROR_MESSAGES.NO_BUSINESS_UNIT}</EmptyMessage>
            </FlexboxContainerComponent>
        )

    if (isLoading) return <LoadingIndicator center />;

    // Determine if auto approve options should be shown based on user selection
    const showAutoApprove = vendorConfiguration.autoApproveEnabled;

    return (
        <FlexboxContainerComponent flexDirection="column">

            <SelectComponent
                label="Brand"
                disabled={!brandList?.length || !!selectedBusinessUnit?.brandId || disabled}
                labelPosition="top"
                options={
                    brandList.map((brand: any) => ({
                        value: brand.name,
                        id: brand.id,
                    })) || []
                }
                onChange={(event) => {
                    const selBrands = event.target.value; // array of selection
                    const selectedItem = event.target.selected;

                    // Get ids of selected brands by name
                    const selectedBrandIds = brandList
                        .filter((brand: any) => selBrands.includes(brand.name))
                        .map((brand: any) => brand.id);

                    if (selBrands.indexOf(selectedItem) > -1) {
                        handleBrandChange(selectedBrandIds);

                    } else {
                        // Filter out the deselected brand
                        const selectedBrandId = brandList
                            .find((brand: any) => brand.name === selectedItem)
                            ?.id;

                        const filteredBrandIds = selectedBrandIds.filter(
                            (brandId: number) => brandId !== selectedBrandId
                        );
                        handleBrandChange(filteredBrandIds);
                    }
                }}
                value={vendorConfiguration.brandIds?.map((brandId: number) => {
                    const brand = brandList.find((brand: any) => brand.id === brandId);
                    return brand ? brand.name : '';
                })}
                defaultValue={ProductLocalization.listing.filter.brandSelect}
                width="30%"
                multiple={true}
                limitTags={2}
                isInvalid={!!errors.brands}
                errorMessage={errors.brands}
                isOptional={false}
                errroLabelPostion="bottom"
                displayAsterisk
            />

            <SectionTitle className='heading'>{SECTION_TITLES.EMAIL_REMINDERS}</SectionTitle>
            <InputContainer>
                <ReadOnlyWrapper readOnly={emailRemindersReadyOnly}>
                    <InputComponent
                        id={INPUT_FIELDS.RENT_REMINDER.ID}
                        type="number"
                        disabled={disabled || emailRemindersReadyOnly}
                        placeholder={INPUT_FIELDS.RENT_REMINDER.PLACEHOLDER}
                        labelName={INPUT_FIELDS.RENT_REMINDER.LABEL}
                        onInputChange={handleRentReminderChange}
                        value={vendorConfiguration.rentReminderDays}
                        width="30%"
                        isInvalid={!!errors.rentReminderDays}
                        errorMessage={errors.rentReminderDays}
                        minValue='1'
                    />
                </ReadOnlyWrapper>
                <Description disabled={disabled}>
                    {INPUT_FIELDS.RENT_REMINDER.DESCRIPTION}
                </Description>
            </InputContainer>

            <SectionTitle className='heading'>{SECTION_TITLES.PAYMENT_REMINDERS}</SectionTitle>

            {/* Inline payment reminder inputs */}
            <InlineInputsContainer>
                <InlineInputBox>
                    <ReadOnlyWrapper readOnly={paymentRemindersReadyOnly}>
                        <InputComponent
                            id={INPUT_FIELDS.PAYMENT_REMINDERS.FIRST.ID}
                            type="number"
                            disabled={disabled || paymentRemindersReadyOnly}
                            isOptional={true}
                            placeholder={INPUT_FIELDS.PAYMENT_REMINDERS.FIRST.PLACEHOLDER}
                            labelName={INPUT_FIELDS.PAYMENT_REMINDERS.FIRST.LABEL}
                            onInputChange={handleFirstPaymentReminderChange}
                            value={vendorConfiguration.firstReminderDays}
                            width="100%"
                            isInvalid={!!errors.firstReminderDays}
                            errorMessage={errors.firstReminderDays}
                            minValue='1'
                        />
                    </ReadOnlyWrapper>
                    <Description disabled={disabled}>
                        {INPUT_FIELDS.PAYMENT_REMINDERS.SHARED_DESCRIPTION}
                    </Description>
                </InlineInputBox>

                <InlineInputBox>
                    <ReadOnlyWrapper readOnly={paymentRemindersReadyOnly}>
                        <InputComponent
                            id={INPUT_FIELDS.PAYMENT_REMINDERS.SECOND.ID}
                            type="number"
                            disabled={disabled || paymentRemindersReadyOnly}
                            isOptional={true}
                            placeholder={INPUT_FIELDS.PAYMENT_REMINDERS.SECOND.PLACEHOLDER}
                            labelName={INPUT_FIELDS.PAYMENT_REMINDERS.SECOND.LABEL}
                            onInputChange={handleSecondPaymentReminderChange}
                            value={vendorConfiguration.secondReminderDays}
                            width="100%"
                            isInvalid={!!errors.secondReminderDays}
                            errorMessage={errors.secondReminderDays}
                            minValue='1'
                        />
                    </ReadOnlyWrapper>
                    <Description disabled={disabled}>
                        {INPUT_FIELDS.PAYMENT_REMINDERS.SHARED_DESCRIPTION}
                    </Description>
                </InlineInputBox>

                <InlineInputBox>
                    <ReadOnlyWrapper readOnly={paymentRemindersReadyOnly}>
                        <InputComponent
                            id={INPUT_FIELDS.PAYMENT_REMINDERS.FINAL.ID}
                            type="number"
                            disabled={disabled || paymentRemindersReadyOnly}
                            isOptional={true}
                            placeholder={INPUT_FIELDS.PAYMENT_REMINDERS.FINAL.PLACEHOLDER}
                            labelName={INPUT_FIELDS.PAYMENT_REMINDERS.FINAL.LABEL}
                            onInputChange={handleFinalPaymentReminderChange}
                            value={vendorConfiguration.thirdReminderDays}
                            width="100%"
                            isInvalid={!!errors.thirdReminderDays}
                            errorMessage={errors.thirdReminderDays}
                            minValue='1'
                        />
                    </ReadOnlyWrapper>
                    <Description disabled={disabled}>
                        {INPUT_FIELDS.PAYMENT_REMINDERS.SHARED_DESCRIPTION}
                    </Description>
                </InlineInputBox>
            </InlineInputsContainer>

            <CheckboxContainer>
                <ReadOnlyWrapper readOnly={paymentRemindersReadyOnly}>
                    <Checkbox
                        id={INPUT_FIELDS.AUTO_APPROVE.CHECKBOX.ID}
                        name={INPUT_FIELDS.AUTO_APPROVE.CHECKBOX.NAME}
                        disabled={disabled || isAutoApproveDisabled || paymentRemindersReadyOnly}
                        label={INPUT_FIELDS.AUTO_APPROVE.CHECKBOX.LABEL}
                        checked={vendorConfiguration.autoApproveEnabled}
                        appearance="none"
                        checkboxSize="s"
                        onChange={handleAutoApproveChange}
                        theme={'brandLight'}
                        bgColor={Main.brandLight}
                    />
                </ReadOnlyWrapper>
                {!!errors.autoApproveEnabled && (
                    <Description style={{ color: Color.error, marginTop: '5px' }}>
                        {errors.autoApproveEnabled}
                    </Description>
                )}
            </CheckboxContainer>

            {showAutoApprove && (
                <InputContainer>
                    <ReadOnlyWrapper readOnly={paymentRemindersReadyOnly}>
                        <InputComponent
                            id={INPUT_FIELDS.AUTO_APPROVE.WAITING_PERIOD.ID}
                            type="number"
                            disabled={disabled || !vendorConfiguration.autoApproveEnabled || paymentRemindersReadyOnly}
                            placeholder={INPUT_FIELDS.AUTO_APPROVE.WAITING_PERIOD.PLACEHOLDER}
                            onInputChange={handleAutoApproveAfterFinalChange}
                            value={vendorConfiguration.autoApproveAfterDays}
                            width="30%"
                            isInvalid={!!errors.autoApproveAfterDays}
                            errorMessage={errors.autoApproveAfterDays}
                            minValue='1'
                        />
                    </ReadOnlyWrapper>
                </InputContainer>
            )}
        </FlexboxContainerComponent>
    );
});

export default EmailConfigurationSettings;